import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { invoices as api, http, apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, Card, EmptyState, Loading, ConfirmDelete } from "@/components/Shared";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileText, Plus, Eye, Pencil, Copy, Trash2, Search } from "lucide-react";

const FILTERS = ["all", "sent", "partial", "paid", "overdue", "draft"];

export default function Invoices() {
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.list().then(setList).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const remove = async (id) => { try { await api.remove(id); toast.success("Invoice deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  const duplicate = async (id) => {
    try { const d = await http.post(`/invoices/${id}/duplicate`).then((r) => r.data); toast.success(`Duplicated as ${d.number}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!list) return <Loading />;
  const filtered = list.filter((i) => (filter === "all" || i.display_status === filter) &&
    (i.number.toLowerCase().includes(q.toLowerCase()) || (i.customer?.name || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div>
      <PageHeader title="Invoices" subtitle="Create, send and track all your invoices.">
        <Button onClick={() => nav("/invoices/new")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="new-invoice-button"><Plus className="w-4 h-4 mr-1.5" /> New Invoice</Button>
      </PageHeader>

      {list.length === 0 ? (
        <EmptyState icon={FileText} title="No invoices yet" description="Create your first invoice to start getting paid."
          action={<Button onClick={() => nav("/invoices/new")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="empty-new-invoice"><Plus className="w-4 h-4 mr-1.5" /> New Invoice</Button>} />
      ) : (
        <Card>
          <div className="p-4 border-b border-slate-100 flex flex-col md:flex-row gap-3 md:items-center justify-between">
            <div className="relative max-w-sm w-full">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoices…" className="pl-9" data-testid="invoice-search" />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {FILTERS.map((f) => (
                <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all ${filter === f ? "bg-[#0B132B] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{f}</button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
                  <th className="px-5 py-3 font-semibold">Invoice</th>
                  <th className="px-5 py-3 font-semibold hidden md:table-cell">Customer</th>
                  <th className="px-5 py-3 font-semibold hidden lg:table-cell">Due</th>
                  <th className="px-5 py-3 font-semibold text-right">Total</th>
                  <th className="px-5 py-3 font-semibold text-center">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((i) => (
                  <tr key={i.id} className="hover:bg-slate-50" data-testid={`invoice-row-${i.id}`}>
                    <td className="px-5 py-3.5"><button onClick={() => nav(`/invoices/${i.id}`)} className="font-semibold text-[#0B132B] hover:text-[#D4AF37]">{i.number}</button><div className="text-xs text-slate-400">{fmtDate(i.issue_date)}</div></td>
                    <td className="px-5 py-3.5 hidden md:table-cell text-slate-600">{i.customer?.name || "—"}</td>
                    <td className="px-5 py-3.5 hidden lg:table-cell text-slate-600">{fmtDate(i.due_date)}</td>
                    <td className="px-5 py-3.5 text-right font-semibold text-[#0B132B]">{money(i.total)}</td>
                    <td className="px-5 py-3.5 text-center"><StatusBadge status={i.display_status} testId={`invoice-status-${i.id}`} /></td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button variant="ghost" size="icon" onClick={() => nav(`/invoices/${i.id}`)} data-testid={`view-invoice-${i.id}`}><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => nav(`/invoices/${i.id}/edit`)} data-testid={`edit-invoice-${i.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => duplicate(i.id)} data-testid={`duplicate-invoice-${i.id}`}><Copy className="w-4 h-4" /></Button>
                        <ConfirmDelete onConfirm={() => remove(i.id)} title="Delete invoice?" trigger={<Button variant="ghost" size="icon" className="text-rose-500" data-testid={`delete-invoice-${i.id}`}><Trash2 className="w-4 h-4" /></Button>} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
